import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { sentryInit, showGitInfo, isDevelopment } from '@pypestream/utils';
import { init as initI18n, LanguageScopes } from '@pypestream/translations';
import 'requestidlecallback-polyfill';

import './index.scss';
import App from './app';
import { getAnalytics } from './hooks/use-analytics';

showGitInfo({
  appName: 'Pro-Studio',
  commitHash: process.env.GIT_COMMIT_HASH,
  branchName: process.env.GIT_BRANCH_NAME,
  tagName: process.env.GIT_TAG_NAME,
  version: process.env.VITE_APP_VERSION,
});

const analytics = getAnalytics();
if (analytics) {
  if (typeof analytics.init === 'function') {
    analytics.init(import.meta.env.FE_POSTHOG_API_KEY, {
      api_host: import.meta.env.FE_POSTHOG_API_HOST,
      session_recording: {
        recordCrossOriginIframes: true,
        maskAllInputs: false,
        maskTextSelector: '[data-private]',
      },
      capture_pageview: true,
      capture_pageleave: true,
    });
  }

  if (typeof analytics.register === 'function') {
    analytics.register({
      'Pypestream Product': 'Pro-studio',
      'Pypestream Environment': import.meta.env.ANALYTICS_CAPTURE_ENVIRONMENT,
      'Git Commit': import.meta.env.ANALYTICS_CAPTURE_GIT_COMMIT,
      'Git Tags': import.meta.env.ANALYTICS_CAPTURE_GIT_TAGS
        ? import.meta.env.ANALYTICS_CAPTURE_GIT_TAGS.split(',')
        : null,
      'GitHub PR ID': import.meta.env.ANALYTICS_CAPTURE_GITHUB_PR_ID,
      'GitHub PR URL': import.meta.env.ANALYTICS_CAPTURE_GITHUB_PR_URL,
    });
  }

  // auto-opt out until we know more about the user
  if (typeof analytics.opt_out_capturing === 'function') {
    analytics.opt_out_capturing();
  }
}
let container: HTMLElement | null = null;

document.addEventListener('DOMContentLoaded', async function () {
  if (!container) {
    container = document.getElementById('root') as HTMLElement;
    const root = createRoot(container);
    await initI18n(LanguageScopes.proStudio);

    const proStudioSentryDSN = import.meta.env
      .FE_PRO_STUDIO_SENTRY_DSN as string;

    if (!isDevelopment && proStudioSentryDSN) {
      sentryInit(proStudioSentryDSN);
    }

    root.render(
      <StrictMode>
        <App />
      </StrictMode>
    );
  }
});
